import type { ContentfulClientApi } from 'contentful'
import type {
  TypeArticleSkeleton,
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeContentPathingSkeleton,
  TypeContentPathingWithoutUnresolvableLinksResponse,
  TypeEventSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
  TypePageBuilderSkeleton,
  TypePageBuilderWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

type ContentfulClient = ContentfulClientApi<undefined>

type JourneyEntry =
  | TypeArticleWithoutUnresolvableLinksResponse
  | TypeEventWithoutUnresolvableLinksResponse
  | TypePageBuilderWithoutUnresolvableLinksResponse

type ContentPathingEntry = TypeContentPathingWithoutUnresolvableLinksResponse

export type ContentJourneyEntryType = 'article' | 'event' | 'pageBuilder'

export interface ContentJourneyItem {
  id: string
  type: ContentJourneyEntryType
  label: string
  route: string
  isCurrent: boolean
}

export interface ContentJourneyData {
  id: string
  title: string
  description: string
  displayStepLabels: boolean
  items: ContentJourneyItem[]
}

export const withContentJourneyQuery = (route: string, journeyId: string) => {
  const hashIndex = route.indexOf('#')
  const hash = hashIndex >= 0 ? route.slice(hashIndex) : ''
  const routeWithoutHash = hashIndex >= 0 ? route.slice(0, hashIndex) : route
  const queryIndex = routeWithoutHash.indexOf('?')
  const path =
    queryIndex >= 0 ? routeWithoutHash.slice(0, queryIndex) : routeWithoutHash
  const existingQuery =
    queryIndex >= 0 ? routeWithoutHash.slice(queryIndex + 1) : ''
  const query = new URLSearchParams(existingQuery)

  query.set('journey', journeyId)

  return `${path}?${query.toString()}${hash}`
}

export const shouldExpandContentJourney = (
  journeyId: string,
  activeJourneyId: string,
) => !activeJourneyId || journeyId === activeJourneyId

interface FetchContentJourneyParams {
  client: ContentfulClient
  locale: string
  currentEntryId: string
  isEntryVisible: (entry: JourneyEntry) => boolean
}

const getContentType = (
  entry: JourneyEntry,
): ContentJourneyEntryType | null => {
  const contentType = entry.sys.contentType?.sys?.id
  return contentType === 'article' ||
    contentType === 'event' ||
    contentType === 'pageBuilder'
    ? contentType
    : null
}

const getPath = (entry: JourneyEntry) => {
  const path = entry.fields?.path
  return typeof path === 'string' ? path.trim() : ''
}

const getLabel = (entry: JourneyEntry) => {
  const fields = entry.fields as {
    entryTitle?: string
    title?: string
    metaTitle?: string
    path?: string
  }

  return (
    fields.title?.trim() ||
    fields.metaTitle?.trim() ||
    fields.entryTitle?.trim() ||
    fields.path?.trim() ||
    entry.sys.id
  )
}

const getRoute = (entry: JourneyEntry, type: ContentJourneyEntryType) => {
  const path = getPath(entry)
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path

  const normalizedPath = path.replace(/^\/+/, '')
  if (type === 'article') return `/news/${normalizedPath}`
  if (type === 'event') return `/event/${normalizedPath}`
  return path === '/' ? '/' : `/${normalizedPath}`
}

const sortPathingEntries = (entries: ContentPathingEntry[]) =>
  entries.slice().sort((a, b) => a.sys.id.localeCompare(b.sys.id))

const getLinkIds = (links: unknown) => {
  if (!Array.isArray(links)) return []
  return links
    .map((link) => (link as { sys?: { id?: string } })?.sys?.id)
    .filter((id): id is string => Boolean(id))
}

const getCuratedPathingIds = (entry: ContentPathingEntry) =>
  getLinkIds(entry.fields?.curatedPathing)

const fetchTargetsByType = async ({
  client,
  locale,
  ids,
  type,
}: {
  client: ContentfulClient
  locale: string
  ids: string[]
  type: ContentJourneyEntryType
}) => {
  if (!ids.length) return [] as JourneyEntry[]

  const baseQuery = {
    locale,
    include: 2,
    limit: 1000,
    'sys.id[in]': ids,
  }

  try {
    if (type === 'article') {
      const response =
        await client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
          ...baseQuery,
          content_type: 'article',
          select: [
            'sys',
            'fields.entryTitle',
            'fields.title',
            'fields.path',
            'fields.publishedDateTime',
            'fields.visibility',
          ],
        })
      return response.items as JourneyEntry[]
    }

    if (type === 'event') {
      const response =
        await client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
          ...baseQuery,
          content_type: 'event',
          select: [
            'sys',
            'fields.entryTitle',
            'fields.title',
            'fields.path',
            'fields.startDatetime',
            'fields.visibility',
          ],
        })
      return response.items as JourneyEntry[]
    }

    const response =
      await client.withoutUnresolvableLinks.getEntries<TypePageBuilderSkeleton>(
        {
          ...baseQuery,
          content_type: 'pageBuilder',
          select: [
            'sys',
            'fields.entryTitle',
            'fields.metaTitle',
            'fields.path',
            'fields.visibility',
          ],
        },
      )
    return response.items as JourneyEntry[]
  } catch {
    // A partially deployed content model should not take down the page.
    return []
  }
}

const fetchTargets = async ({
  client,
  locale,
  ids,
}: {
  client: ContentfulClient
  locale: string
  ids: string[]
}) => {
  const [articles, events, pages] = await Promise.all(
    (['article', 'event', 'pageBuilder'] as ContentJourneyEntryType[]).map(
      (type) => fetchTargetsByType({ client, locale, ids, type }),
    ),
  )

  return [...articles, ...events, ...pages]
}

const buildJourney = ({
  pathing,
  targets,
  currentEntryId,
  isEntryVisible,
}: {
  pathing: ContentPathingEntry
  targets: JourneyEntry[]
  currentEntryId: string
  isEntryVisible: (entry: JourneyEntry) => boolean
}): ContentJourneyData | null => {
  const targetById = new Map(
    targets
      .filter((entry) => isEntryVisible(entry))
      .map((entry) => [entry.sys.id, entry]),
  )
  const curatedIds = getCuratedPathingIds(pathing)
  const orderedIds = curatedIds.length
    ? curatedIds
    : targets.map((entry) => entry.sys.id)
  const seen = new Set<string>()
  const items: ContentJourneyItem[] = []

  orderedIds.forEach((id) => {
    if (seen.has(id)) return
    const entry = targetById.get(id)
    if (!entry) return
    const type = getContentType(entry)
    if (!type) return
    const route = getRoute(entry, type)
    if (!route) return

    seen.add(id)
    items.push({
      id,
      type,
      label: getLabel(entry),
      route,
      isCurrent: id === currentEntryId,
    })
  })

  if (!items.length || !items.some((item) => item.isCurrent)) return null

  return {
    id: pathing.sys.id,
    title:
      pathing.fields?.pathTitle?.trim() ||
      pathing.fields?.entryTitle?.trim() ||
      pathing.sys.id,
    description: pathing.fields?.pathDescription?.trim() || '',
    displayStepLabels: pathing.fields?.displayStepLabels === true,
    items,
  }
}

const fetchPathingEntries = async ({
  client,
  locale,
  query,
}: {
  client: ContentfulClient
  locale: string
  query: Record<string, unknown>
}) => {
  try {
    const response =
      await client.withoutLinkResolution.getEntries<TypeContentPathingSkeleton>(
        {
          content_type: 'contentPathing',
          locale,
          include: 0,
          limit: 100,
          ...query,
        },
      )
    return response.items as ContentPathingEntry[]
  } catch {
    return [] as ContentPathingEntry[]
  }
}

export async function fetchContentJourneys({
  client,
  locale,
  currentEntryId,
  isEntryVisible,
}: FetchContentJourneyParams): Promise<ContentJourneyData[]> {
  if (!currentEntryId) return []

  const curatedPathing = sortPathingEntries(
    await fetchPathingEntries({
      client,
      locale,
      query: { 'fields.curatedPathing.sys.id': currentEntryId },
    }),
  ).filter((entry) => getCuratedPathingIds(entry).includes(currentEntryId))

  if (curatedPathing.length) {
    const journeys = await Promise.all(
      curatedPathing.map(async (pathing) => {
        const targets = await fetchTargets({
          client,
          locale,
          ids: getCuratedPathingIds(pathing),
        })
        return buildJourney({
          pathing,
          targets,
          currentEntryId,
          isEntryVisible,
        })
      }),
    )

    return journeys.filter((journey): journey is ContentJourneyData =>
      Boolean(journey),
    )
  }

  return []
}

export async function fetchContentJourney(
  params: FetchContentJourneyParams,
): Promise<ContentJourneyData | null> {
  return (await fetchContentJourneys(params))[0] || null
}
